import { apiFetch, apiNoContentTypeFetch, type CustomFetchOptions } from "./index";
import { getApiBaseUrl } from "./baseUrl";

const API_BASE = getApiBaseUrl();
const BASE = `${API_BASE}/api/management/future-board`;

export type FutureCardStatus = "draft" | "visible" | "archived";
export type FutureFeeling = "anticipation" | "distant" | "review" | "unspoken";

// 画像は、運営が用意したプリセット（type: "preset"）か、Pro の自分の写真（type: "upload"）。
// url は表示用、thumb_url は一覧用の署名付き URL（期限切れ・署名の失敗のときは null）。
export interface FutureImage {
  id: number;
  type: "upload" | "preset";
  position: number | null;
  preset_key?: string;
  category?: string;
  alt_text_en?: string;
  url: string | null;
  thumb_url: string | null;
  width: number | null;
  height: number | null;
  alt_text: string | null;
  focal_x: number;
  focal_y: number;
}

// 変更履歴の対象になる、文章の項目。
export interface FutureCardText {
  title: string;
  meaning: string | null;
  self_image: string | null;
  scene: string | null;
  domains: string[];
  sensations: string[];
}

export interface FutureCard extends FutureCardText {
  id: number;
  status: FutureCardStatus;
  related_goal_id: number | null;
  sort_order: number;
  version: number;
  created_at: string;
  updated_at: string;
  /** 詳細の取得・更新の応答にだけ含まれる */
  images?: FutureImage[];
}

export interface FutureCardSummary {
  id: number;
  status: FutureCardStatus;
  title: string;
  meaning_excerpt: string | null;
  domains: string[];
  related_goal_id: number | null;
  sort_order: number;
  version: number;
  updated_at: string;
  /** 表紙（サムネイルのみ。url は null） */
  cover: FutureImage | null;
}

export interface FutureCardList {
  cards: FutureCardSummary[];
  pinned_card_id: number | null;
  board_version: number;
}

export interface FuturePreset {
  key: string;
  category: string;
  alt_text: string;
  alt_text_en: string;
  width: number;
  height: number;
  url: string | null;
  thumb_url: string | null;
}

export interface FutureCheckin {
  id: number;
  card_id: number;
  card_version: number;
  /** サーバーが決めた日付（YYYY-MM-DD） */
  local_date: string;
  /** その日付を決めたときの基準（IANA 名。現在は Asia/Tokyo） */
  timezone: string;
  feeling: FutureFeeling;
  memo: string | null;
  created_at: string;
  updated_at: string;
}

export interface FutureRevision {
  version: number;
  snapshot: FutureCardText;
  created_at: string;
}

/** カードの最終的な画像の並び（先頭が表紙。最大3枚） */
export type FutureImageSelection =
  | { type: "upload"; image_id: number }
  | { type: "preset"; preset_key: string };

export interface CreateFutureCardInput extends Partial<Omit<FutureCardText, "title">> {
  /** 作成の再送で重複させないための ID（再試行では同じ値を使う） */
  client_request_id: string;
  title: string;
  related_goal_id?: number | null;
}

export interface UpdateFutureCardInput extends Partial<FutureCardText> {
  /** 取得したときの版。古いと 409（VERSION_CONFLICT） */
  expected_version: number;
  related_goal_id?: number | null;
  images?: FutureImageSelection[];
}

export interface UpdateFutureImageInput {
  alt_text?: string | null;
  focal_x?: number;
  focal_y?: number;
}

type RequestOptions = Pick<CustomFetchOptions, "hideGlobalLoader">;

function withAuthorization(token: string): HeadersInit {
  return { Authorization: `Bearer ${token}` };
}

function json(token: string): HeadersInit {
  return { ...withAuthorization(token), "Content-Type": "application/json" };
}

// --- カード ---

export function listFutureCards(
  token: string,
  status?: FutureCardStatus,
  options: RequestOptions = {},
): Promise<FutureCardList> {
  const query = status ? `?status=${encodeURIComponent(status)}` : "";
  return apiFetch(`${BASE}/cards${query}`, { headers: withAuthorization(token), hideGlobalLoader: options.hideGlobalLoader });
}

export function getFutureCard(token: string, id: number, options: RequestOptions = {}): Promise<{ card: FutureCard }> {
  return apiFetch(`${BASE}/cards/${id}`, { headers: withAuthorization(token), hideGlobalLoader: options.hideGlobalLoader });
}

export function createFutureCard(token: string, input: CreateFutureCardInput): Promise<{ card: FutureCard }> {
  return apiFetch(`${BASE}/cards`, { method: "POST", headers: json(token), body: JSON.stringify(input) });
}

export function updateFutureCard(token: string, id: number, input: UpdateFutureCardInput): Promise<{ card: FutureCard }> {
  return apiFetch(`${BASE}/cards/${id}`, { method: "PATCH", headers: json(token), body: JSON.stringify(input) });
}

export function changeFutureCardStatus(
  token: string,
  id: number,
  status: FutureCardStatus,
  expectedVersion: number,
): Promise<{ card: FutureCard }> {
  return apiFetch(`${BASE}/cards/${id}/status`, {
    method: "POST",
    headers: json(token),
    body: JSON.stringify({ status, expected_version: expectedVersion }),
  });
}

/** 完全削除。確認の操作を経たときだけ呼ぶこと（アーカイブとは別の操作）。存在しなくても成功する。 */
export function deleteFutureCard(token: string, id: number): Promise<Record<string, never>> {
  return apiFetch(`${BASE}/cards/${id}`, { method: "DELETE", headers: json(token), body: JSON.stringify({ confirm: true }) });
}

/** ids は、表示中のカードすべてを並べた配列でなければならない（欠け・余りは 422） */
export function reorderFutureCards(
  token: string,
  ids: number[],
  expectedBoardVersion: number,
): Promise<{ board_version: number }> {
  return apiFetch(`${BASE}/cards/order`, {
    method: "PUT",
    headers: json(token),
    body: JSON.stringify({ ids, expected_board_version: expectedBoardVersion }),
  });
}

/** 今日の一枚。null で未選択に戻す。表示中のカードだけ指定できる。 */
export function setPinnedFutureCard(
  token: string,
  cardId: number | null,
): Promise<{ pinned_card_id: number | null; board_version: number }> {
  return apiFetch(`${BASE}/pinned`, { method: "PUT", headers: json(token), body: JSON.stringify({ card_id: cardId }) });
}

export function listFutureCardRevisions(token: string, id: number): Promise<{ revisions: FutureRevision[] }> {
  return apiFetch(`${BASE}/cards/${id}/revisions`, { headers: withAuthorization(token) });
}

// --- 画像 ---

/** 利用者が「用意された画像から選ぶ」を押したときだけ呼ぶ（自動の推薦はしない） */
export function listFuturePresets(token: string, options: RequestOptions = {}): Promise<{ presets: FuturePreset[] }> {
  return apiFetch(`${BASE}/presets`, { headers: withAuthorization(token), hideGlobalLoader: options.hideGlobalLoader });
}

/** 自分の写真の登録（Pro のみ。Free は 403 UPLOAD_REQUIRES_PRO）。1MB 以下の JPEG・PNG・WebP。 */
export function uploadFutureImage(token: string, file: any): Promise<{ image: FutureImage }> {
  const formData = new FormData();
  formData.append("image", file);
  return apiNoContentTypeFetch(`${BASE}/images`, { method: "POST", headers: withAuthorization(token), body: formData });
}

export function updateFutureImage(token: string, id: number, input: UpdateFutureImageInput): Promise<{ image: FutureImage }> {
  return apiFetch(`${BASE}/images/${id}`, { method: "PATCH", headers: json(token), body: JSON.stringify(input) });
}

export function deleteFutureImage(token: string, id: number): Promise<Record<string, never>> {
  return apiFetch(`${BASE}/images/${id}`, { method: "DELETE", headers: withAuthorization(token) });
}

// --- 気持ちの記録 ---

/** 日付は送らない（サーバーが決める）。同じ日の再回答は更新になる。 */
export function recordFutureCheckin(
  token: string,
  cardId: number,
  input: { feeling: FutureFeeling; memo?: string | null },
): Promise<{ checkin: FutureCheckin }> {
  return apiFetch(`${BASE}/cards/${cardId}/checkin`, { method: "PUT", headers: json(token), body: JSON.stringify(input) });
}

export function listFutureCheckins(token: string, cardId: number): Promise<{ checkins: FutureCheckin[]; timezone: string }> {
  return apiFetch(`${BASE}/cards/${cardId}/checkins`, { headers: withAuthorization(token) });
}

export function deleteFutureCheckin(token: string, cardId: number, checkinId: number): Promise<Record<string, never>> {
  return apiFetch(`${BASE}/cards/${cardId}/checkins/${checkinId}`, { method: "DELETE", headers: withAuthorization(token) });
}

// --- AI 支援（「ヒントをもらう」）。本人が押したときだけ呼ぶ。候補か質問を返すだけで、カードは更新しない ---

export interface AssistRequestBody {
  mode: "explore" | "self_image" | "scene" | "polish";
  language: "ja" | "en";
  card_id?: number;
  /** 整える項目（polish） */
  field?: "title" | "meaning" | "self_image" | "scene";
  /** いま入力している内容（未保存の分を含む）。依頼の対象として渡すだけで、保存はしない */
  draft?: { title?: string; meaning?: string; self_image?: string; scene?: string; sensations?: string[] };
  answers?: Array<{ question: string; answer: string }>;
  note?: string;
}

export type AssistResult =
  | { kind: "question"; question: string }
  | { kind: "candidates"; field: "title" | "meaning" | "self_image" | "scene"; candidates: string[]; dropped: number }
  | { kind: "none"; field: "title" | "meaning" | "self_image" | "scene"; dropped: number };

export type AssistSubmitResponse =
  | { job_id: string; existing?: boolean }
  | { cached: true; result: AssistResult };

export type AssistJobStatus =
  | { status: "pending"; poll_after_seconds?: number }
  | { status: "completed"; result: AssistResult }
  | { status: "failed"; error: string };

export interface AssistQuota {
  enabled: boolean;
  daily_limit?: number;
  used?: number;
  remaining?: number;
  /** 1日の上限が戻る時刻（ISO） */
  resets_at?: string;
  /** resets_at の基準のタイムゾーン（IANA 名） */
  timezone?: string;
  min_interval_seconds?: number;
  retry_after_seconds?: number;
}

/** 画面の案内用（使えるか・残りの回数）。LLM は呼ばず、回数も消費しない */
export function getFutureAssistQuota(token: string, options: RequestOptions = {}): Promise<AssistQuota> {
  return apiFetch(`${BASE}/ai/quota`, { headers: withAuthorization(token), hideGlobalLoader: options.hideGlobalLoader ?? true });
}

// --- AI トーク（「この未来について話す」）の会話の履歴。LLM は呼ばず、回数も消費しない ---

export interface TalkHistoryMessage {
  id: number;
  role: "user" | "assistant";
  content: string;
  created_at: string;
}

/** カードごとの会話（古い順、直近 40 件）。送信は postFutureTalk（api/aiTalk） */
export function getFutureTalkHistory(token: string, cardId: number, options: RequestOptions = {}): Promise<{ messages: TalkHistoryMessage[] }> {
  return apiFetch(`${API_BASE}/api/management/ai-talk/history?future_card_id=${encodeURIComponent(String(cardId))}`, {
    headers: withAuthorization(token), hideGlobalLoader: options.hideGlobalLoader ?? true,
  });
}

export function requestFutureAssist(token: string, body: AssistRequestBody): Promise<AssistSubmitResponse> {
  return apiFetch(`${BASE}/ai/assist`, { method: "POST", headers: json(token), body: JSON.stringify(body), hideGlobalLoader: true });
}

export function getFutureAssistJob(token: string, jobId: string): Promise<AssistJobStatus> {
  return apiFetch(`${BASE}/ai/assist/${encodeURIComponent(jobId)}`, { headers: withAuthorization(token), hideGlobalLoader: true });
}
